import { gql, useQuery } from '@apollo/client';
import useLoginState from '../../core/hooks/useLoginState';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import notEmpty from '../../core/util/notEmpty';

const productBackorders = gql`
    query productBackorders(
        $skuList: [String!]
        $language: Language!
        $store: ID!
    ) {
        orderItems(
            skuList: $skuList
            language: $language
            store: $store
            pendingOnly: true
        ) {
            items {
                id
                sku
                pendingQuantity
                requestedShippingDate
                stockAvailableNow
                stockAvailableSoon
                stockAvailableDelay
                stockAvailableLater
                backorderMarginDays
                futureDeliveries {
                    amount
                    calendarWeek
                    year
                }
            }
        }
    }
`;

export interface FutureDelivery {
    amount: number;
    calendarWeek: number;
    year: number;
}

export interface ProductBackorder {
    sku: string;
    pendingQuantity: number;
    requestedShippingDate?: string | null;
    stockAvailableNow: number;
    stockAvailableSoon: number;
    stockAvailableDelay: number;
    stockAvailableLater: number;
    backorderMarginDays?: number;
    futureDeliveries: FutureDelivery[];
}

const useProductBackorders = (skuList: string[]) => {
    const isLoggedIn = useLoginState();
    const { store, language } = useStoreAndLanguageParams();
    const { data, loading, error } = useQuery<{
        orderItems: {
            items: ProductBackorder[];
        };
    }>(productBackorders, {
        variables: {
            store,
            language,
            skuList,
        },
        skip: !isLoggedIn || !skuList.filter(notEmpty).length,
    });
    if (error) {
        throw error;
    }
    return {
        loading,
        data: data?.orderItems.items,
    };
};

export default useProductBackorders;
